// Split a single entered quantity across a consolidated group's source work
// orders. Fills the earliest-due WO first (undated last; ties broken by WO
// number), capping each at its remaining need.
//
// ``capacity`` (optional) maps step_id -> how many that WO can actually make
// right now (its own tree's material — un-issued production is earmarked per
// order, so one WO's made sub-assemblies can't feed a sibling's tree). When
// given, a first pass fills each WO only up to what it can make; anything
// left then falls through to the remaining need in the same order, so an
// over-ask still lands somewhere and the pre-flight names the shortage.
// Missing / null capacity = unknown, treated as uncapped.
//
// Input: array of shop-floor step records; total quantity (number).
// Output: { allocations: [{ step, quantity }], leftover }
//   - leftover > 0 means the operator asked for more than the group needs.

function naturalSort(a, b) {
  return String(a || '').localeCompare(String(b || ''), undefined, { numeric: true });
}

// Fill order: earliest due first, undated last, ties by WO number. Exported
// so the group dialog can LIST its rows in the same order the quantity
// fills them — otherwise the auto-split looks like it scatters randomly.
export function compareByDueThenNumber(a, b) {
  const da = a?.due_date ? new Date(a.due_date).getTime() : null;
  const db = b?.due_date ? new Date(b.due_date).getTime() : null;
  if (da !== null && db !== null && da !== db) return da - db;
  if (da === null && db !== null) return 1;
  if (da !== null && db === null) return -1;
  return naturalSort(a?.wo_number, b?.wo_number);
}

export function allocateGroupQuantity(sources, totalQty, capacity = null) {
  const qty = Number(totalQty);
  if (!Array.isArray(sources) || !Number.isFinite(qty) || qty <= 0) {
    return { allocations: [], leftover: Math.max(0, qty || 0) };
  }

  const eligible = sources
    .filter((s) => s && (s.status === 'planned' || s.status === 'in_progress'))
    .map((s) => {
      const total = parseFloat(s.quantity_to_produce) || 0;
      const done = parseFloat(s.quantity_completed) || 0;
      return { step: s, remaining: Math.max(0, total - done) };
    })
    .filter((e) => e.remaining > 0)
    .sort((a, b) => compareByDueThenNumber(a.step, b.step));

  const capOf = (e) => {
    const c = capacity ? capacity[e.step.step_id] : null;
    return c == null || !Number.isFinite(Number(c))
      ? e.remaining
      : Math.max(0, Math.min(e.remaining, Number(c)));
  };

  let left = qty;
  const taken = new Map();
  // Pass 1: what each WO can actually make; pass 2: the rest of the need.
  [capOf, (e) => e.remaining].forEach((limit) => {
    for (const e of eligible) {
      if (left <= 0) break;
      const already = taken.get(e) || 0;
      const take = Math.min(Math.max(0, limit(e) - already), left);
      if (take > 0) {
        taken.set(e, already + take);
        left -= take;
      }
    }
  });
  const allocations = eligible
    .filter((e) => taken.has(e))
    .map((e) => ({ step: e.step, quantity: taken.get(e) }));
  return { allocations, leftover: left };
}

export default allocateGroupQuantity;
